import { ChevronLeftIcon, CloseIcon } from '~components/icons'
import { useNavigate } from 'react-router-dom'
import { BREAKPOINTS, FONTS, SPACINGS, COLORS } from './theme'
import { ClickPoint } from './click-point'
import { typographyStyles } from './typography-styles'
import { Button, Typography } from '@mui/material'

// eslint-disable-next-line
import styled from 'styled-components'

interface NotificationProps {
  $notificationVariant: NotificationVariant
}

export const Headline = styled(Typography)<NotificationProps>`
  ${typographyStyles.paragraphBold1};
  color: ${({ $notificationVariant }) => getHeaderColor($notificationVariant)};
  text-transform: uppercase;
  margin: 0;
  line-height: ${FONTS.lineHeights.md};
  text-align: center;

  @media only screen and (${BREAKPOINTS.md}) {
    font-size: ${FONTS.sizes.xl};
    font-family: ${FONTS.families.headlines.bold};
    letter-spacing: 2px;
  }
`

export const NotificationHeaderContainer = styled.div`
  display: flex;
  justify-content: center;

  @media only screen and (${BREAKPOINTS.md}) {
    margin-bottom: ${SPACINGS[1]};
  }
`

export const StyledClickPoint = styled(ClickPoint)`
  position: absolute;
  top: 8px;
  right: 8px;

  @media only screen and (${BREAKPOINTS.md}) {
    top: 12px;
    right: 16px;
  }
`

export const NotificationContainer = styled.div<NotificationProps>`
  position: relative;
  padding: ${({ $notificationVariant }) =>
    $notificationVariant === 'pageFail'
      ? `${SPACINGS[4]} ${SPACINGS[5]}`
      : `12px ${SPACINGS[1]}`};

  border-radius: 4px;
  min-width: 360px;
  max-width: ${({ $notificationVariant }) =>
    $notificationVariant === 'pageFail' ? '952px' : '972px'};
  width: 100%;
  background-color: ${({ $notificationVariant }) =>
    getBackgroundColor($notificationVariant)};
  box-sizing: border-box;

  @media only screen and (${BREAKPOINTS.md}) {
    padding: ${({ $notificationVariant }) =>
      $notificationVariant === 'pageFail'
        ? `${SPACINGS[5]} 100px`
        : `${SPACINGS[3]} ${SPACINGS[2]}`};
  }
`

export const NotificationContent = styled.div`
  text-align: center;
`

export const StyledTypography = styled(Typography)`
  margin: 0;
`

export const GoBackButton = styled(Button)`
  margin-top: ${SPACINGS[3]};
  width: 200px;
  @media only screen and (${BREAKPOINTS.md}) {
    width: unset;
  }
`

export function getHeaderColor(variant: NotificationVariant) {
  switch (variant) {
    case 'warning':
      return COLORS.orange.dark.primary
    case 'error':
      return COLORS.red.dark.primary
    case 'info':
    case 'success':
    case 'pageFail':
      return COLORS.secondary.darkBlue.primary
  }
}

export function getBackgroundColor(variant: NotificationVariant) {
  switch (variant) {
    case 'success':
      return COLORS.citrus.mid.tint3
    case 'warning':
      return COLORS.orange.tint5
    case 'error':
      return COLORS.red.mid.tint3
    case 'info':
      return COLORS.secondary.notificationBlue
    case 'pageFail':
      return COLORS.secondary.lightBlue.tint
  }
}

export type NotificationVariant =
  'info' | 'error' | 'warning' | 'success' | 'pageFail'

export interface NotificationPropsOne {
  variant: NotificationVariant
  headline: string
  text: string
  onClick?: () => void
  className?: string
}

export function Notification({
  variant,
  text,
  headline,
  onClick,
  className,
}: NotificationPropsOne) {
  const navigate = useNavigate()

  return (
    <NotificationContainer $notificationVariant={variant} className={className}>
      <NotificationHeaderContainer>
        <Headline variant="h2" $notificationVariant={variant}>
          {headline}
        </Headline>
        {onClick && (
          <StyledClickPoint ariaLabel="close" onClick={onClick}>
            <CloseIcon />
          </StyledClickPoint>
        )}
      </NotificationHeaderContainer>
      <NotificationContent>
        <Typography
          component="article"
          variant="paragraph1"
          dangerouslySetInnerHTML={{ __html: text }}
        />
        {variant === 'pageFail' && (
          <GoBackButton
            variant="contained"
            startIcon={<ChevronLeftIcon fontSize={20} />}
            onClick={() => navigate(-1)}
          >
            Go Back
          </GoBackButton>
        )}
      </NotificationContent>
    </NotificationContainer>
  )
}

Notification.displayName = 'Notification'
